import React, {
  useCallback, useEffect, useReducer, useState,
} from 'react';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import PropTypes from 'prop-types';

import useEvent from '@sp/core/helper/hooks/useEvent';

import { COOKIE_SETTINGS, LCZ_STRINGS } from '../constants';
import { DEV_ERRORS } from '../constants/local';
import CookieManager from '../CookieManager';

import CookieNoticeToolbar from './CookieNoticeToolbar';
import CookieSettingsPopup from './CookieSettingsPopup';

import './index.scss';

const CookieNotice = ({
  storage,
  onAccept,
  onReject,
  canOpenPopup,
  modalContainer: ModalContainer,
  onOpenToolbar,
  onCloseToolbar,
  infoUrl,
  infoTarget,
  lczStrings,
  toolbarText,
  themes,
  disabledLinks,
  popupClassName,
}) => {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const settings = storage?.settings;

  useEffect(() => {
    if (settings) {
      storage.noticeRef = {
        forceUpdate,
      };
    } else {
      throw new Error(DEV_ERRORS.NOT_INITIALIZED);
    }

    return () => {
      storage.noticeRef = null;
    };
  }, []);
  const handleAccept = useEvent(() => {
    const result = storage.setCookies();

    forceUpdate();
    setIsModalOpen(false);

    if (isFunction(onAccept)) {
      onAccept(result);
    }
  });
  const handleReject = useEvent(() => {
    const result = storage.setCookies(0);

    forceUpdate();
    setIsModalOpen(false);

    if (isFunction(onReject)) {
      onReject(result);
    }
  });
  const handleSave = useEvent((settings) => {
    const settingsToSave = Object.entries(settings)
      .reduce(
        (acc, [setting, value]) => {
          const settingName = setting.toUpperCase();

          if (value !== undefined) {
            acc[settingName] = value;
          }

          return acc;
        },
        {}
      );

    storage.saveSettings(settingsToSave);
    // apply the settings immediately on save click
    handleAccept();
  });
  const toggleModal = useCallback((bool) => () => {
    if (!canOpenPopup) return;

    setIsModalOpen(bool);
  }, [canOpenPopup]);

  const functionality = settings[COOKIE_SETTINGS.FUNCTIONALITY];
  const analytics = settings[COOKIE_SETTINGS.ANALYTICS];
  const showToolbar = storage.shouldShowToolbar();

  if (!showToolbar) return null;

  return (
    <>
      {showToolbar && (
        <CookieNoticeToolbar
          disabledLinks={disabledLinks}
          onAccept={handleAccept}
          onReject={handleReject}
          onOpenModal={toggleModal(true)}
          lczStrings={lczStrings}
          toolbarText={toolbarText}
          infoTarget={infoTarget}
          infoUrl={infoUrl}
          onOpenToolbar={onOpenToolbar}
          onCloseToolbar={onCloseToolbar}
          themes={themes}
        />
      )}
      <ModalContainer
        onClose={toggleModal(false)}
        isOpened={isModalOpen}
        className="cookie-settings-popup__wrap"
      >
        <CookieSettingsPopup
          disabledLinks={disabledLinks}
          lczStrings={lczStrings}
          onSave={handleSave}
          functionality={functionality}
          analytics={analytics}
          infoUrl={infoUrl}
          infoTarget={infoTarget}
          className={popupClassName}
        />
      </ModalContainer>
    </>
  );
};

CookieNotice.propTypes = {
  /**
   * Component-wrapper for the modal content
   * Should implement required interface of componentV2/modal, e.g: isOpened, onClose
   */
  modalContainer: PropTypes.func.isRequired,

  /**
   * link for more details about privacy policy and cookies
   */
  infoUrl: PropTypes.string,

  /**
   * Specify where linked privacy policy document is loaded
   */
  infoTarget: PropTypes.string,

  /**
   * Callback function that fires after user clicks 'Accept'
   */
  onAccept: PropTypes.func,

  /**
   * Callback function that fires after user clicks 'Reject'
   */
  onReject: PropTypes.func,

  /**
   * Object which contains localized strings. If provided it will override the defaults.
   */
  lczStrings: PropTypes.object,

  /**
   * Will be called on toolbar open
   */
  onOpenToolbar: PropTypes.func,

  /**
   * Will be called on toolbar close
   */
  onCloseToolbar: PropTypes.func,

  /**
   * Text to display on toolbar
   */
  toolbarText: PropTypes.string,

  /**
   * Themes for cookie banner
   */
  themes: PropTypes.object,

  /**
   * Make links disabled
   */
  disabledLinks: PropTypes.bool,
};

CookieNotice.defaultProps = {
  storage: CookieManager,
  lczStrings: LCZ_STRINGS,
  infoTarget: '_blank',
  themes: {
    bannerTheme: '',
    buttonTheme: '',
  },
  canOpenPopup: true,
  disabledLinks: false,
  onAccept: null,
  onReject: null,
};

export default React.memo(CookieNotice, isEqual);
